import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import { Button, Grid, Typography, Box } from '@mui/material';
import { Trash2 } from 'lucide-react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import CommonLayout from '../components/CommonLayout';
import PermissionsManager from '../components/PermissionsManager';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import EntraChangePasswordDialog from '../components/EntraChangePasswordDialog';
import SecurityShield from '../components/SecurityShield';
import { canChangeEntraPassword } from '../auth/runtimeAuth';
import Loader from '../components/Loader';
import LoadingSubmitButton from '../components/LoadingSubmitButton';
import DeleteConfirmationDialog from '../components/dialogs/DeleteConfirmationDialog';
import DepartmentSelectionDialog from '../components/DepartmentSelectionDialog';
import InputWithIcon from '../components/InputWithIcon';
import DropdownWithIcon from '../components/DropdownWithIcon';
import PageTitle from '../components/PageTitle';
import userIcon from '../assets/users/user.svg';
import emailIcon from '../assets/users/email.svg';
import phoneIcon from '../assets/users/phone.svg';
import roleIcon from '../assets/protocols/department.svg';
import departmentIcon from '../assets/protocols/department.svg';
import hospitalIcon from '../assets/protocols/hospital.svg';
import usersRoleIcon from '../assets/users/role.svg';
import { hospitalRoles } from '../config/hospitalRoles';
import { cancelButton, confirmButton, deleteButtonInTitleMenu, pageActionsContainer } from '../components/Buttons';
import TwoStepVerification from '../components/TwoStepVerification';
import { isMfaAvailable } from '../auth/runtimeAuth';


const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const theme = createTheme({ typography: { fontFamily: '"Rubik", sans-serif' },
  direction: 'rtl',
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: '#103C6E',
          color: 'white',
          '&:hover': {
            backgroundColor: '#0d3157',
          },
          '&:disabled': {
            backgroundColor: '#B9EDE7',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          textAlign: 'right',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          transform: 'translate(-14px, -9px) scale(0.75)',
          '&[data-shrink="false"]': {
            transform: 'translate(14px, 16px) scale(1)',
          },
        },
      },
    },
  },
});

function EditUser() {
  const { user: loggedInUser, hospitals, refreshUserData, fetchHospitals, authMethod } = useContext(UserContext);
  const [user, setUser] = useState({
    name: '',
    email: '',
    phone: '',
    isSuperAdmin: false,
    roles: [{ hospital: '', role: 'USER' }]
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
 
   const location = useLocation();
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const id = location.pathname === '/profile' ? loggedInUser?._id : params.id;
  const doesEditMyself = id === loggedInUser?._id;
  const canEdit = loggedInUser?.isSuperAdmin || doesEditMyself;
 
   useEffect(() => {
     const fetchData = async () => {
      setIsLoading(true);
      try {
        const userResponse = await API.get(`/api/users/${id}`);
       
        // Keep the populated hospital and department objects as they are
        setUser({
          ...userResponse.data,
          password: '',
        });

        // Fetch pending requests if on profile page
        if (location.pathname === '/profile') {
           fetchPendingRequests();
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, location.pathname, loggedInUser?._id]);

  const handleRoleChange = (index, hospital, department, r) => {
    const newRoles = user.roles.map((role, idx) => {
      if (idx === index) {
        return { ...role, 'hospital': hospital, 'department': department, 'role': r };
      }
      return role;
    });
    setUser({ ...user, roles: newRoles });
  };

  const addRole = () => {
    setUser({ ...user, roles: [...user.roles, { hospital: '', role: 'USER' }] });
  };

  const removeRole = index => {
    setUser({ ...user, roles: user.roles.filter((_, idx) => idx !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(user.email)) {
      setEmailError('נא הזן כתובת אימייל תקינה');
      return;
    }
    setEmailError('');
    
    setIsSubmitting(true);
    try {
      let userToSubmit = { ...user };
      
      // Convert populated hospital and department objects back to IDs for submission
      if (userToSubmit.roles) {
        userToSubmit.roles = userToSubmit.roles.map(role => ({
          ...role,
          hospital: role.hospital?._id || role.hospital,
          department: role.department?._id || role.department
        }));
      }
      
      if (doesEditMyself) {
        // eslint-disable-next-line no-unused-vars
        const { roles, ...userWithoutRoles } = userToSubmit;
        userToSubmit = userWithoutRoles;
      }
      await API.put(`/api/users/${id}`, userToSubmit);
      navigate(-1);
    } catch (error) {
      console.error('Updating user failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
 
   const handleSuperAdminChange = (value) => {
     setUser({ ...user, isSuperAdmin: value});
   };
 
   const handleDeleteSuccess = () => {
    navigate('/users');
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/api/users/${id}`);
      setDeleteConfirmOpen(false);
      handleDeleteSuccess();
    } catch (error) {
      console.error('Deletion failed:', error);
      const message = error.response?.data?.message || 'שגיאה במחיקת המשתמש. אנא נסה שנית.';
      setErrorMessage(message);
      setShowError(true);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const pendingResponse = await API.get('/api/users/pending-requests');
      // Filter for requests made by the logged-in user
      const userPendingRequests = pendingResponse.data.filter(req =>
        req.requestedBy?._id === loggedInUser._id || req.requestedBy === loggedInUser._id
      );
      setPendingRequests(userPendingRequests);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  const isExternalUser = Boolean(user.signInMethods?.length);
  // Until the user is loaded the placeholder looks like a password account, which
  // would show "change password" for a moment to a Microsoft user.
  const isUserLoaded = Boolean(id) && user._id === id;
  // A Microsoft account's password lives in Entra, so only its owner can change it,
  // and only with the current password; a Google sign-in through Microsoft has none.
  // Legacy passwords can also be set by a super admin.
  const canChangePassword = isUserLoaded && (isExternalUser
    ? doesEditMyself && canChangeEntraPassword()
    : (loggedInUser.isSuperAdmin || loggedInUser._id === id));

  const handleDepartmentSuccess = async () => {
    await refreshUserData();
    await fetchHospitals();
    try {
      const userResponse = await API.get(`/api/users/${id}`);
      setUser({
        ...userResponse.data,
        password: '',
      });

      // Refresh pending requests
      if (location.pathname === '/profile') {
        fetchPendingRequests();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };


  // Component to display department assignments - only for profile page
  const DepartmentAssignmentsView = () => {
    // Only show on profile page
    if (location.pathname !== '/profile') {
      return null;
    }

    // Filter out roles without departments and super admin
    const departmentRoles = user.roles ? user.roles.filter(role => role.department && !user.isSuperAdmin) : [];
    
    // Process pending requests with hospital and department info
    const pendingWithInfo = pendingRequests.map(req => {
      let hospital = null;
      let department = null;
      
      // Handle hospital which could be a populated object or just an _id string
      if (req.hospital) {
        if (typeof req.hospital === 'object' && req.hospital._id) {
          // Hospital is populated - use it directly
          hospital = req.hospital;
        } else {
          // Hospital is just an ID - find it in hospitals array
          hospital = hospitals.find(h => h._id === req.hospital);
        }
      }
      
      // Handle department which could be a populated object or just an _id string
      if (req.department) {
        if (typeof req.department === 'object' && req.department._id) {
          // Department is populated - use it directly
          department = req.department;
        } else {
          // Department is just an ID - find it in hospital's departments
          const departmentId = req.department;
          department = hospital?.departments?.find(d => d._id === departmentId);
        }
      }
      
      return {
        hospital: hospital?.nameHebrew || 'לא זמין',
        department: department?.nameHebrew || 'לא זמין',
        type: req.type === 'invite-user-to-department' ? 'invited' : 'requested',
        isPending: true
      };
    });

    // Get full department and hospital info
    const assignmentsWithInfo = departmentRoles.map(role => {
      let hospital = null;
      let department = null;
      
      // Handle hospital which could be a populated object or just an _id string
      if (role.hospital) {
        if (typeof role.hospital === 'object' && role.hospital._id) {
          // Hospital is populated - use it directly
          hospital = role.hospital;
        } else {
          // Hospital is just an ID - find it in hospitals array
          hospital = hospitals.find(h => h._id === role.hospital);
        }
      }
      
      // Handle department which could be a populated object or just an _id string
      if (role.department) {
        if (typeof role.department === 'object' && role.department._id) {
          // Department is populated - use it directly
          department = role.department;
        } else {
          // Department is just an ID - find it in hospital's departments
          const departmentId = role.department;
          department = hospital?.departments?.find(d => d._id === departmentId);
        }
      }
      
      return {
        hospital: hospital?.nameHebrew || 'לא זמין',
        department: department?.nameHebrew || 'לא זמין',
        role: role.role === 'USER' ? 'משתמש' : role.role === 'EDITOR' ? 'עורך' : 'מנהל'
      };
    });

    // Helper function to render a single card
    const renderCard = (assignment, isPending = false, index) => {
      const cardStyles = {
        flex: '1 1 calc(33.333% - 16px)',
        minWidth: '280px',
        maxWidth: '310px',
        padding: '20px',
        backgroundColor: isPending ? '#fff7ed' : '#ffffff',
        borderRadius: '12px',
        border: isPending ? '1px solid #fed7aa' : '1px solid #e8e8e8',
        boxShadow: isPending ? '0 2px 8px rgba(251, 146, 60, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s ease',
        opacity: isPending ? 0.85 : 1,
      };

      return (
        <Box 
          key={index}
          style={cardStyles}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = isPending 
              ? '0 4px 12px rgba(251, 146, 60, 0.15)' 
              : '0 4px 12px rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = isPending 
              ? '0 2px 8px rgba(251, 146, 60, 0.1)' 
              : '0 2px 8px rgba(0, 0, 0, 0.06)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
            <img src={hospitalIcon} alt="hospital" style={{ width: '20px', marginLeft: '10px', opacity: isPending ? 0.6 : 0.7 }} />
            <Typography style={{ fontSize: '0.9rem', color: '#666' }}>בית חולים</Typography>
          </Box>
          <Typography style={{ fontWeight: 'bold', marginBottom: '16px', fontSize: '1rem', color: '#103C6E', opacity: isPending ? 0.8 : 1 }}>
            {assignment.hospital}
          </Typography>
          
          <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <img src={departmentIcon} alt="department" style={{ width: '18px', marginLeft: '10px', opacity: isPending ? 0.6 : 0.7 }} />
            <Typography style={{ fontSize: '0.9rem', color: '#666' }}>מחלקה</Typography>
          </Box>
          <Typography style={{ marginBottom: '16px', fontSize: '0.95rem', opacity: isPending ? 0.8 : 1 }}>
            {assignment.department}
          </Typography>
          
          {/* Pending Badge or Role Badge */}
          {isPending ? (
            <Box style={{ 
              display: 'inline-block', 
              padding: '6px 12px', 
              backgroundColor: '#fb923c', 
              color: 'white',
              borderRadius: '6px',
              fontSize: '0.85rem',
              fontWeight: '600',
            }}>
              {assignment.type === 'invited' ? 'הוזמן למחלקה' : 'ממתין לאישור'}
            </Box>
          ) : assignment.role ? (
            <Box style={{ 
              display: 'inline-block', 
              padding: '6px 12px', 
              backgroundColor: '#f0f9ff', 
              borderRadius: '6px',
              border: '1px solid #bae6fd'
            }}>
              <Box style={{ display: 'flex', alignItems: 'center' }}>
                <img src={usersRoleIcon} alt="role" style={{ width: '16px', marginLeft: '6px', opacity: 0.7 }} />
                <Typography style={{ fontSize: '0.85rem', color: '#0369a1', fontWeight: '500' }}>
                  {assignment.role}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
      );
    };

    // Combine all assignments (approved + pending)
    const allAssignments = [
      ...assignmentsWithInfo.map(assignment => ({ ...assignment, isPending: false })),
      ...pendingWithInfo
    ];

    return (
      <Grid item xs={12} style={{ marginTop: '20px' }}>
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Typography variant="h6">{allAssignments.length === 0 ? "המשתמש לא משוייך לאף מחלקה" : "הרשאות מחלקה"}</Typography>
          <Button
            variant="contained"
            onClick={() => setDepartmentDialogOpen(true)}
            style={{
              backgroundColor: '#1FB5A3',
              color: 'white',
              padding: '10px 24px',
              borderRadius: '8px',
            }}
          >
            + הצטרפות למחלקה
          </Button>
        </Box>
        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          {allAssignments.map((assignment, index) => renderCard(assignment, assignment.isPending, index))}
        </Box>
      </Grid>
    );
  };

  return (
    <CommonLayout>
      <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={theme}>
          <div className="edit-user-container" style={{ position: 'relative' }}>
            <PageTitle title={location.pathname === '/profile' ? 'עריכת פרופיל' : 'עריכת משתמש'}>
              {/* Only once the migration applies to the viewer: listed users before
                  the cutoff, everyone from it on (docs/EntraFullMigrationPlan.md). */}
              {loggedInUser?.entraMigrationVisible && (
                <SecurityShield
                  // Your own level from the session, which turning on two-step
                  // verification refreshes; someone else's from their record.
                  level={doesEditMyself ? loggedInUser?.securityLevel : (isUserLoaded ? user.securityLevel : undefined)}
                  canUpgradeToSilver={doesEditMyself && authMethod === 'legacy' && Boolean(loggedInUser?.canMoveToEntra)}
                  canUpgradeToGold={doesEditMyself && isExternalUser && isMfaAvailable()}
                />
              )}
              {loggedInUser.isSuperAdmin && (
                <button onClick={() => setDeleteConfirmOpen(true)} style={deleteButtonInTitleMenu} title="מחק משתמש">
                  <Trash2 size={16} />
                </button>
              )}
            </PageTitle>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <InputWithIcon
                    icon={userIcon}
                    placeholder="שם"
                    value={user.name}
                    onChange={(e) => setUser({ ...user, name: e.target.value })}
                    required
                    disabled={!canEdit}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <InputWithIcon
                      icon={emailIcon}
                      placeholder="אימייל"
                      value={user.email}
                      onChange={(e) => {
                        setUser({ ...user, email: e.target.value });
                        if (emailError) {
                          setEmailError('');
                        }
                      }}
                      required
                      disabled={!loggedInUser.isSuperAdmin || doesEditMyself}
                      style={{ flexGrow: 1, borderColor: emailError ? 'red' : '' }}
                      error={!!emailError}
                      helperText={emailError}
                    />
                    {emailError && <p style={{ color: 'red', fontSize: 'smaller' }}>{emailError}</p>}
                    {canChangePassword && (
                      <Button
                        variant="contained"
                        onClick={() => setOpenPasswordDialog(true)}
                        style={{
                          marginRight: '20px',
                          minWidth: '120px',
                          backgroundColor: '#1FB5A3',
                        }}
                      >
                        שנה סיסמה
                      </Button>
                    )}
                  </Box>
                </Grid>
                {isExternalUser && doesEditMyself && isMfaAvailable() && (
                  <Grid item xs={12}>
                    <TwoStepVerification />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <InputWithIcon
                    icon={phoneIcon}
                    placeholder="טלפון"
                    value={user.phone}
                    onChange={(e) => setUser({ ...user, phone: e.target.value })}
                    disabled={!canEdit}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DropdownWithIcon
                    icon={roleIcon}
                    placeholder="תפקיד בבית חולים"
                    value={user.hospitalRole || ''}
                    onChange={(e) => setUser({ ...user, hospitalRole: e.target.value })}
                    disabled={!canEdit}
                  >
                    {hospitalRoles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </DropdownWithIcon>
                </Grid>
                <DepartmentAssignmentsView />
                {!doesEditMyself && loggedInUser.isSuperAdmin && <PermissionsManager
                  roles={user.roles}
                  hospitals={hospitals}
                  loggedInSuperAdmin={loggedInUser.isSuperAdmin}
                  isSuperAdmin={user.isSuperAdmin}
                  onChangeSuperAdmin={handleSuperAdminChange}
                  onRoleChange={handleRoleChange}
                  onAddRole={addRole}
                  onRemoveRole={removeRole}
                />}
                <Grid item xs={12} className="submit-button-container" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                {(loggedInUser.isSuperAdmin || doesEditMyself) && (
                  <>
                    <div style={pageActionsContainer}>
                      <LoadingSubmitButton
                        type="submit"
                        style={confirmButton}
                        isLoading={isSubmitting}
                        text={id ? 'עדכן' : 'צור'}
                        loadingText={id ? 'מעדכן...' : 'יוצר...'}
                        disabled={isSubmitting}
                      />
                      <button
                        type="button"
                        style={cancelButton}
                        onClick={() => navigate(-1)}
                        disabled={isSubmitting}
                      >
                        ביטול
                      </button>
                  </div>
                  </>
                )}
                </Grid>
              </Grid>
            </form>
 
            <DeleteConfirmationDialog
              open={deleteConfirmOpen}
              onClose={() => setDeleteConfirmOpen(false)}
              onConfirm={handleDelete}
              title={`האם אתה בטוח שברצונך למחוק את המשתמש ${user.name}?`}
              showError={showError}
              errorMessage={errorMessage}
              onCloseError={() => setShowError(false)}
            />

            {isExternalUser ? (
              <EntraChangePasswordDialog
                open={openPasswordDialog}
                onClose={() => setOpenPasswordDialog(false)}
              />
            ) : (
              <ChangePasswordDialog
                open={openPasswordDialog}
                onClose={() => setOpenPasswordDialog(false)}
                userId={id}
              />
            )}

            <DepartmentSelectionDialog
              open={departmentDialogOpen}
              onClose={() => setDepartmentDialogOpen(false)}
              onSuccess={handleDepartmentSuccess}
            />

            <Loader isLoading={isLoading} />
          </div>
        </ThemeProvider>
      </CacheProvider>
    </CommonLayout>
  );
}

export default EditUser;